import React, { useState, useEffect, useCallback } from 'react';
import {
  FiCreditCard,
  FiRefreshCw,
  FiSave,
  FiCheck,
  FiX,
  FiInfo,
  FiSmartphone,
  FiDollarSign,
  FiBriefcase,
} from 'react-icons/fi';
import { agencyPaymentMethodAPI } from '../../services/api';

// Icônes associées à chaque slug de méthode de paiement
const METHOD_ICONS = {
  'orange-money': FiSmartphone,
  'moov-money': FiSmartphone,
  wave: FiSmartphone,
  'virement-bancaire': FiBriefcase,
  cheque: FiDollarSign,
  'carte-bancaire': FiCreditCard,
  especes: FiDollarSign,
};

// Champs de configuration par slug (détails spécifiques à renseigner)
const METHOD_FIELDS = {
  'orange-money': [
    { key: 'phone', label: 'Numéro Orange Money', placeholder: 'ex: +223 70 00 00 00', type: 'text' },
    { key: 'instructions', label: 'Instructions', placeholder: 'ex: Indiquez la référence dans le libellé', type: 'textarea' },
  ],
  'moov-money': [
    { key: 'phone', label: 'Numéro Moov Money', placeholder: 'ex: +223 60 00 00 00', type: 'text' },
    { key: 'instructions', label: 'Instructions', placeholder: 'ex: Indiquez la référence dans le libellé', type: 'textarea' },
  ],
  wave: [
    { key: 'phone', label: 'Numéro Wave', placeholder: 'ex: +223 70 00 00 00', type: 'text' },
    { key: 'instructions', label: 'Instructions', placeholder: 'ex: Envoyez le ticket après paiement', type: 'textarea' },
  ],
  'virement-bancaire': [
    { key: 'bank_name', label: 'Nom de la banque', placeholder: 'ex: BMS-Sahel', type: 'text' },
    { key: 'account_holder', label: 'Titulaire du compte', placeholder: 'ex: Agence Immobilière SARL', type: 'text' },
    { key: 'iban', label: 'IBAN / RIB', placeholder: 'ex: ML01 XXXX XXXX XXXX', type: 'text' },
    { key: 'instructions', label: 'Instructions', placeholder: 'ex: Indiquez le numéro de facture en référence', type: 'textarea' },
  ],
  cheque: [
    { key: 'payable_to', label: 'Ordre du chèque', placeholder: 'ex: Agence Immobilière SARL', type: 'text' },
    { key: 'instructions', label: 'Instructions', placeholder: 'ex: À déposer en agence', type: 'textarea' },
  ],
  'carte-bancaire': [
    { key: 'provider', label: 'Fournisseur (Stripe, etc.)', placeholder: 'ex: Stripe', type: 'text' },
    { key: 'instructions', label: 'Instructions', placeholder: 'ex: Paiement sécurisé en ligne', type: 'textarea' },
  ],
  especes: [
    { key: 'instructions', label: 'Instructions', placeholder: 'ex: Paiement en agence aux heures d\'ouverture', type: 'textarea' },
  ],
};

const getMethodIcon = (slug) => METHOD_ICONS[slug] || FiCreditCard;
const getMethodFields = (slug) => METHOD_FIELDS[slug] || [];

const AgencyPaymentMethodsPage = () => {
  const [allMethods, setAllMethods] = useState([]);
  const [configured, setConfigured] = useState({}); // map payment_method_id -> { is_active, details }
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [allRes, cfgRes] = await Promise.all([
        agencyPaymentMethodAPI.listAll(),
        agencyPaymentMethodAPI.list(),
      ]);
      const all = Array.isArray(allRes.data) ? allRes.data : [];
      const cfg = Array.isArray(cfgRes.data) ? cfgRes.data : [];
      setAllMethods(all);
      const map = {};
      cfg.forEach((c) => {
        map[c.payment_method_id] = {
          is_active: c.is_active,
          details: c.details || {},
        };
      });
      setConfigured(map);
    } catch (err) {
      console.error('Error loading payment methods:', err);
      setError(err.response?.data?.error || 'Impossible de charger les méthodes de paiement.');
    } finally {
      setLoading(false);
    }
  }, []);

useEffect(() => { loadData(); }, [loadData]);
 
const isActive = (id) => Boolean(configured[id]?.is_active);

  const toggleActive = (id) => {
    setSuccess(false);
    setConfigured((prev) => ({
      ...prev,
      [id]: {
        is_active: !(prev[id]?.is_active ?? false),
        details: prev[id]?.details || {},
      },
    }));
  };

  const updateDetail = (id, key, value) => {
    setSuccess(false);
    setConfigured((prev) => ({
      ...prev,
      [id]: {
        is_active: prev[id]?.is_active ?? false,
        details: { ...(prev[id]?.details || {}), [key]: value },
      },
    }));
  };

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    setSuccess(false);
    try {
      const methods = allMethods.map((m) => ({
        payment_method_id: m.id,
        is_active: isActive(m.id),
        details: configured[m.id]?.details || {},
      }));
      await agencyPaymentMethodAPI.update(methods);
      setSuccess(true);
    } catch (err) {
      console.error('Error saving payment methods:', err);
      setError(err.response?.data?.error || 'Erreur lors de l\'enregistrement.');
    } finally {
      setSaving(false);
    }
  };

  const activeCount = allMethods.filter((m) => isActive(m.id)).length;

  return (
    <div>
      <div className="page-header">
        <div>
          <h1>Méthodes de paiement de l'agence</h1>
          <p style={{ color: 'var(--text-secondary)', marginTop: 4 }}>
            Configurez les moyens de paiement proposés à vos locataires et clients.
          </p>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button className="btn btn-secondary" onClick={loadData} disabled={loading || saving}>
            <FiRefreshCw /> Actualiser
          </button>
          <button className="btn btn-primary" onClick={handleSave} disabled={loading || saving}>
            <FiSave /> {saving ? 'Enregistrement...' : 'Enregistrer'}
          </button>
        </div>
      </div>

      {error && <div className="alert alert-error">{error}</div>}
      {success && (
        <div className="alert alert-success">
          <FiCheck style={{ marginRight: 6 }} /> Configuration enregistrée avec succès.
        </div>
      )}

      {/* Résumé */}
      <div className="stats-grid" style={{ gridTemplateColumns: 'repeat(3, 1fr)', marginBottom: 24 }}>
        <div className="card">
          <div className="card-body" style={{ textAlign: 'center', padding: 16 }}>
            <div style={{ fontSize: 28, fontWeight: 700, color: 'var(--primary-color)' }}>{allMethods.length}</div>
            <div style={{ color: 'var(--text-secondary)', fontSize: 13 }}>Méthodes disponibles</div>
          </div>
        </div>
        <div className="card">
          <div className="card-body" style={{ textAlign: 'center', padding: 16 }}>
            <div style={{ fontSize: 28, fontWeight: 700, color: 'var(--success)' }}>{activeCount}</div>
            <div style={{ color: 'var(--text-secondary)', fontSize: 13 }}>Activées</div>
          </div>
        </div>
        <div className="card">
          <div className="card-body" style={{ textAlign: 'center', padding: 16 }}>
            <div style={{ fontSize: 28, fontWeight: 700, color: 'var(--text-secondary)' }}>{allMethods.length - activeCount}</div>
            <div style={{ color: 'var(--text-secondary)', fontSize: 13 }}>Désactivées</div>
          </div>
        </div>
      </div>

      {loading ? (
        <div className="card">
          <div className="card-body" style={{ textAlign: 'center', padding: 40 }}>
            <div className="spinner" />
          </div>
        </div>
      ) : allMethods.length === 0 ? (
        <div className="card">
          <div className="card-body" style={{ textAlign: 'center', padding: 40 }}>
            <FiInfo size={32} style={{ marginBottom: 8, color: 'var(--text-secondary)' }} />
            <h3>Aucune méthode de paiement</h3>
            <p style={{ color: 'var(--text-secondary)' }}>
              Aucune méthode de paiement n'est disponible sur la plateforme.
            </p>
          </div>
        </div>
      ) : (
        <div className="grid-2">
          {allMethods.map((method) => {
            const Icon = getMethodIcon(method.slug);
            const fields = getMethodFields(method.slug);
            const active = isActive(method.id);
            const cfg = configured[method.id]?.details || {};
            return (
              <div
                key={method.id}
                className="card"
                style={{
                  borderColor: active ? 'var(--success)' : 'var(--border)',
                  opacity: active ? 1 : 0.85,
                }}
              >
                <div className="card-body">
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                      <div
                        style={{
                          width: 42,
                          height: 42,
                          borderRadius: 10,
                          background: active ? 'var(--primary-light)' : 'var(--bg)',
                          color: active ? 'var(--primary)' : 'var(--text-secondary)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: 20,
                        }}
                      >
                        <Icon />
                      </div>
                      <div>
                        <div style={{ fontWeight: 600, fontSize: 15 }}>{method.name}</div>
                        {method.description && (
                          <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>{method.description}</div>
                        )}
                      </div>
                    </div>
                    <button
                      type="button"
                      className={`toggle-switch ${active ? 'on' : ''}`}
                      onClick={() => toggleActive(method.id)}
                      aria-label={active ? 'Désactiver' : 'Activer'}
                      title={active ? 'Désactiver' : 'Activer'}
                    >
                      <span className="toggle-knob" />
                    </button>
                  </div>

                  {active && (
                    <div style={{ borderTop: '1px solid var(--border)', paddingTop: 12, marginTop: 4 }}>
                      {fields.length === 0 ? (
                        <p style={{ fontSize: 13, color: 'var(--text-secondary)', margin: 0 }}>
                          Aucune configuration requise pour cette méthode.
                        </p>
                      ) : (
                        fields.map((field) => (
                          <div className="form-group" key={field.key} style={{ marginBottom: 10 }}>
                            <label style={{ fontSize: 13 }}>{field.label}</label>
                            {field.type === 'textarea' ? (
                              <textarea
                                className="form-control"
                                rows="2"
                                value={cfg[field.key] || ''}
                                placeholder={field.placeholder}
                                onChange={(e) => updateDetail(method.id, field.key, e.target.value)}
                              />
                            ) : (
                              <input
                                type="text"
                                className="form-control"
                                value={cfg[field.key] || ''}
                                placeholder={field.placeholder}
                                onChange={(e) => updateDetail(method.id, field.key, e.target.value)}
                              />
                            )}
                          </div>
                        ))
                      )}
                    </div>
                  )}

                  <div style={{ marginTop: 8, fontSize: 12, color: active ? 'var(--success)' : 'var(--text-secondary)', display: 'flex', alignItems: 'center', gap: 6 }}>
                    {active ? <><FiCheck /> Méthode active</> : <><FiX /> Méthode inactive</>}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default AgencyPaymentMethodsPage;